import { useEffect, useRef, useState, useCallback } from "react";
import {
	Page,
	Layout,
	Card,
	Tabs,
	IndexTable,
	Badge,
	Button,
	Banner,
	SkeletonPage,
	SkeletonBodyText,
	Text,
	TextField,
	Select,
	Tooltip,
	useIndexResourceState,
	BlockStack,
	InlineStack,
	Link,
} from "@shopify/polaris";
import { Modal, TitleBar } from "@shopify/app-bridge-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { authenticatedFetch, ApiError, getShopDomain } from "../api";
import type {
	AutoTranslateRules,
	Product,
	ProductsResponse,
	ProductCountsResponse,
	TranslationConfig,
} from "../types";
import { EntitlementBanner, useUsage } from "../components/EntitlementBanner";
import { formatNextRun, type ScheduleInfo } from "../lib/schedule";

type NotInstalledState = { installUrl: string };

type TabId = "all" | "pending" | "translated" | "untranslated";
type BulkTagResponse = {
	success: boolean;
	processed: number;
};

interface TriggerResponse {
	message?: string;
	run_id?: string;
}

interface TranslateStatusResponse {
	lastRunDate?: string;
	runCount?: number;
	frequency?: string;
	activeRun?: boolean;
}

const TABS = [
	{ id: "all" as TabId, content: "All" },
	{ id: "pending" as TabId, content: "Pending Translation" },
	{ id: "translated" as TabId, content: "Translated" },
	{ id: "untranslated" as TabId, content: "Not Translated" },
];

const ENGINE_OPTIONS = [
	{ label: "Use default (from config)", value: "" },
	{ label: "DeepL", value: "deepl" },
	{ label: "OpenAI", value: "openai" },
];

const POLL_INTERVAL_MS = 10_000;

const MAX_PRODUCTS_TOOLTIP =
	"Number of pending products to process in this run. The run stops once it hits this cap.";
const ENGINE_OVERRIDE_TOOLTIP =
	"Translation service to use for this run only. Leave it on the default to use your saved configuration.";

function translationStatusBadge(
	product: Product,
	isRunning = false,
): React.ReactNode {
	if (product.verifiedTranslation) {
		const { state, locale } = product.verifiedTranslation;
		const labels = { complete: "Complete", outdated: "Outdated", missing: "Missing", unavailable: "Unavailable" };
		return <Badge tone={state === "complete" ? "success" : state === "unavailable" ? "critical" : "warning"}>{`${locale.toUpperCase()}: ${labels[state]}`}</Badge>;
	}
	const hasPending = product.tags.includes("translate-pending");
	const translatedTags = product.tags.filter((t) =>
		t.startsWith("translated:"),
	);

	if (isRunning && hasPending) {
		return (
			<Badge tone="info" progress="partiallyComplete">
				Running
			</Badge>
		);
	}
	if (hasPending) {
		return <Badge tone="warning">Pending</Badge>;
	}
	if (translatedTags.length > 0) {
		return <Badge tone="success">Translated</Badge>;
	}
	return <Badge tone="new">Not translated</Badge>;
}

function normaliseList(values?: string[]): string[] {
	return (values ?? []).map((value) => value.trim()).filter(Boolean);
}

function includesCaseInsensitive(values: string[], needle?: string): boolean {
	const normalisedNeedle = (needle ?? "").trim().toLowerCase();
	return values.some((value) => value.toLowerCase() === normalisedNeedle);
}

function isWithinDays(
	dateStr: string | undefined,
	days: number | null | undefined,
): boolean {
	if (!dateStr || !days) return true;
	const timestamp = Date.parse(dateStr);
	if (Number.isNaN(timestamp)) return true;
	const cutoff = Date.now() - Number(days) * 24 * 60 * 60 * 1000;
	return timestamp >= cutoff;
}

function hasExplicitSearchRule(rules: AutoTranslateRules): boolean {
	const vendors = normaliseList(
		rules.vendors?.length
			? rules.vendors
			: rules.vendor
				? [rules.vendor]
				: [],
	);
	const productTypes = normaliseList(
		rules.product_types?.length
			? rules.product_types
			: rules.product_type
				? [rules.product_type]
				: [],
	);

	return Boolean(
		rules.status ||
			vendors.length ||
			normaliseList(rules.vendors_exclude).length ||
			productTypes.length ||
			normaliseList(rules.product_types_exclude).length ||
			normaliseList(rules.include_tags).length ||
			normaliseList(rules.exclude_tags).length ||
			rules.created_within_days ||
			rules.updated_within_days,
	);
}

function autoTranslateIneligibilityReasons(
	product: Product,
	rules: AutoTranslateRules | null,
): string[] {
	if (!rules || !product.tags.includes("translate-pending")) return [];

	const reasons: string[] = [];
	const tags = product.tags.map((tag) => tag.toLowerCase());
	const vendors = normaliseList(
		rules.vendors?.length
			? rules.vendors
			: rules.vendor
				? [rules.vendor]
				: [],
	);
	const vendorExcludes = normaliseList(rules.vendors_exclude);
	const productTypes = normaliseList(
		rules.product_types?.length
			? rules.product_types
			: rules.product_type
				? [rules.product_type]
				: [],
	);
	const productTypeExcludes = normaliseList(rules.product_types_exclude);
	const includeTags = normaliseList(rules.include_tags);
	const excludeTags = normaliseList(rules.exclude_tags);

	if (
		rules.status &&
		product.status?.toLowerCase() !== rules.status.toLowerCase()
	) {
		reasons.push(`Product status must be ${rules.status.toLowerCase()}.`);
	} else if (
		!rules.status &&
		!hasExplicitSearchRule(rules) &&
		product.status?.toLowerCase() !== "active"
	) {
		reasons.push("Product status must be active.");
	}
	if (vendors.length && !includesCaseInsensitive(vendors, product.vendor)) {
		reasons.push("Vendor does not match the Auto-Translate rules.");
	}
	if (
		vendorExcludes.length &&
		includesCaseInsensitive(vendorExcludes, product.vendor)
	) {
		reasons.push("Vendor is excluded by the Auto-Translate rules.");
	}
	if (
		productTypes.length &&
		!includesCaseInsensitive(productTypes, product.productType)
	) {
		reasons.push("Product type does not match the Auto-Translate rules.");
	}
	if (
		productTypeExcludes.length &&
		includesCaseInsensitive(productTypeExcludes, product.productType)
	) {
		reasons.push("Product type is excluded by the Auto-Translate rules.");
	}
	const missingTags = includeTags.filter(
		(tag) => !tags.includes(tag.toLowerCase()),
	);
	if (missingTags.length > 0) {
		reasons.push(
			`Missing required tag${missingTags.length === 1 ? "" : "s"}: ${missingTags.join(", ")}.`,
		);
	}
	const blockedTags = excludeTags.filter((tag) =>
		tags.includes(tag.toLowerCase()),
	);
	if (blockedTags.length > 0) {
		reasons.push(
			`Has excluded tag${blockedTags.length === 1 ? "" : "s"}: ${blockedTags.join(", ")}.`,
		);
	}
	if (!isWithinDays(product.createdAt, rules.created_within_days)) {
		reasons.push(`Created more than ${rules.created_within_days} days ago.`);
	}
	if (!isWithinDays(product.updatedAt, rules.updated_within_days)) {
		reasons.push(`Updated more than ${rules.updated_within_days} days ago.`);
	}
	if (rules.min_price) {
		const price = Number(product.maxVariantPrice);
		if (!Number.isNaN(price) && price < Number(rules.min_price)) {
			reasons.push(`Price is below ${rules.min_price}.`);
		}
	}
	if (rules.require_description && product.hasDescription === false) {
		reasons.push("Missing product description.");
	}

	return reasons;
}

function buildTranslateAndAdaptUrl(
	shop: string | null,
	productGid: string,
	locale: string | null,
): string | null {
	if (!shop) return null;
	const handle = shop.replace(/\.myshopify\.com$/, "");
	const numericId = productGid.split("/").pop();
	if (!numericId) return null;
	const base = `https://admin.shopify.com/store/${handle}/apps/translate-and-adapt/localize/product?id=${numericId}`;
	return locale ? `${base}&shopLocale=${encodeURIComponent(locale)}` : base;
}

function formatDate(dateStr: string): string {
	if (!dateStr) return "—";
	try {
		return new Date(dateStr).toLocaleDateString("en-GB", {
			day: "2-digit",
			month: "short",
			year: "numeric",
		});
	} catch {
		return dateStr;
	}
}

function syncSafeHelp(triggerTag?: string, metafields?: string[]): string {
	const covered = metafields?.length
		? `the title, the description and these metafields: ${metafields.join(", ")}`
		: "the title and the description";
	return `Add the ${triggerTag ?? "enrollment"} tag in your content manager and use Push to Shopify. The translator runs every hour from about 06:45 to 19:45 (Amsterdam time) and once at night, and translates ${covered}. The status tag follows within the next hour. A push during the day usually shows as complete within 1.5 hours; a push in the evening shows as complete the next morning. Counts follow the status tags; each row verifies the current translation.`;
}

export default function Products() {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const selectedTab = Math.max(0, TABS.findIndex((tab) => tab.id === searchParams.get("status")));
	const [catalogMode, setCatalogMode] = useState<string | null>(null);
	const [counts, setCounts] = useState<ProductCountsResponse | null>(null);
	const [countsError, setCountsError] = useState(false);
	const syncSafe = catalogMode === "sync-safe";
	const canManage = catalogMode === "tag-flip";
	const tabs = TABS.map((tab) => ({ ...tab,
		content: syncSafe ? ({ all: "All", pending: "Enrolled · incomplete", translated: "Enrolled · complete", untranslated: "Not enrolled" })[tab.id] : tab.content,
		badge: countsError ? "Unavailable" : counts ? `${counts.counts[tab.id].precision === "AT_LEAST" ? "≥" : ""}${counts.counts[tab.id].count.toLocaleString("en-GB")}` : "…",
	}));
	const refreshCounts = useCallback(async () => {
		try {
			const res = await authenticatedFetch<ProductCountsResponse>("/api/products/counts");
			setCounts(res); setCatalogMode(res.triggerMode); setCountsError(false);
		} catch { setCounts(null); setCountsError(true); }
	}, []);
	const [products, setProducts] = useState<Product[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [notInstalled, setNotInstalled] = useState<NotInstalledState | null>(
		null,
	);
	const [actionError, setActionError] = useState<string | null>(null);
	const [actionNotice, setActionNotice] = useState<string | null>(null);
	const [cursor, setCursor] = useState<string | undefined>(undefined);
	const [hasNextPage, setHasNextPage] = useState(false);
	const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
	const [targetLocale, setTargetLocale] = useState<string | null>(null);

	// Auto-translate status (read-only here — managed on /auto-translate page)
	const [autoTranslateEnabled, setAutoTranslateEnabled] = useState(false);
	const [autoTranslateRules, setAutoTranslateRules] =
		useState<AutoTranslateRules | null>(null);
	const [schedule, setSchedule] = useState<ScheduleInfo | null>(null);

	// Run translation flow (was its own page)
	const [runConfirmOpen, setRunConfirmOpen] = useState(false);
	const [maxProducts, setMaxProducts] = useState("50");
	const [engineOverride, setEngineOverride] = useState("");
	const [triggering, setTriggering] = useState(false);
	const [runError, setRunError] = useState<string | null>(null);
	const [runSuccess, setRunSuccess] = useState<string | null>(null);
	const [activeRun, setActiveRun] = useState(false);
	const [runningIds, setRunningIds] = useState<Set<string>>(new Set());

	const { usage, refresh: refreshUsage } = useUsage();
	const [shopDomain, setShopDomain] = useState(getShopDomain);

	const tabId = TABS[selectedTab]?.id ?? "all";

	const {
		selectedResources,
		allResourcesSelected,
		handleSelectionChange,
		clearSelection,
	} = useIndexResourceState(products as unknown as { [key: string]: unknown }[]);

	const fetchProducts = useCallback(
		async (status: TabId, nextCursor?: string, append = false) => {
			try {
				const params = new URLSearchParams({ status });
				if (nextCursor) params.set("cursor", nextCursor);
				const res = await authenticatedFetch<ProductsResponse>(
					`/api/products?${params.toString()}`,
				);
				setCatalogMode(res.triggerMode);
				setTargetLocale(res.statusLocale);
				setProducts((prev) =>
					append ? [...prev, ...res.products] : res.products,
				);
				setHasNextPage(res.pageInfo.hasNextPage);
				setCursor(res.pageInfo.endCursor);
			} catch (err: unknown) {
				if (
					err instanceof ApiError &&
					err.reason === "not_installed" &&
					err.installUrl
				) {
					setNotInstalled({ installUrl: err.installUrl });
					return;
				}
				setError(err instanceof Error ? err.message : "Failed to load products");
			}
		},
		[],
	);

	useEffect(() => {
		refreshCounts();
		setLoading(true);
		setError(null);
		setNotInstalled(null);
		setProducts([]);
		setCursor(undefined);
		fetchProducts(tabId).finally(() => setLoading(false));
	}, [tabId, fetchProducts, refreshCounts]);

	useEffect(() => {
		authenticatedFetch<{ shop: string }>("/api/session").then((res) => setShopDomain(res.shop)).catch(() => {});
		authenticatedFetch<TranslationConfig>("/api/config")
			.then((res) => {

				setAutoTranslateEnabled(res.auto_translate?.enabled ?? false);
				setAutoTranslateRules(
					res.auto_translate?.enabled ? res.auto_translate.rules : null,
				);
			})
			.catch(() => {
				// Non-fatal: link will fall back to no preselected locale.
			});

		authenticatedFetch<ScheduleInfo>("/api/schedule")
			.then(setSchedule)
			.catch(() => {
				// Non-fatal: banner just hides next-run timestamp.
			});
	}, []);

	// Poll translate status; when activeRun flips false, refetch products so
	// freshly-translated rows move out of Pending.
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

	const fetchStatus = useCallback(async () => {
		try {
			const res = await authenticatedFetch<TranslateStatusResponse>(
				"/api/translate/status",
			);
			setActiveRun((prev) => {
				const next = res.activeRun ?? false;
				if (prev && !next) {
					// Run just finished — refresh visible list and usage.
					fetchProducts(tabId);
					refreshUsage();
					setRunningIds(new Set());
				}
				return next;
			});
		} catch {
			// silently swallow polling errors
		}
	}, [fetchProducts, refreshUsage, tabId]);

	useEffect(() => {
		// Pick up an in-flight run on mount.
		fetchStatus();
	}, [fetchStatus]);

	useEffect(() => {
		if (activeRun) {
			pollRef.current = setInterval(fetchStatus, POLL_INTERVAL_MS);
		} else if (pollRef.current) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
		return () => {
			if (pollRef.current) clearInterval(pollRef.current);
		};
	}, [activeRun, fetchStatus]);

	async function handleLoadMore() {
		if (!cursor || loadingMore) return;
		setLoadingMore(true);
		await fetchProducts(tabId, cursor, true);
		setLoadingMore(false);
	}

	async function bulkAction(action: "mark" | "clear") {
		setActionError(null);
		setActionNotice(null);
		const ids = selectedResources;
		if (ids.length === 0) return;

		const body =
			action === "mark"
				? { product_ids: ids, add: ["translate-pending"], remove: [] }
				: { product_ids: ids, add: [], remove: ["translate-pending"] };

		try {
			const result = await authenticatedFetch<BulkTagResponse>(
				"/api/products/bulk-tag",
				{ method: "POST", body: JSON.stringify(body) },
			);
			if (action === "mark") {
				setActionNotice(
					`${result.processed} product${result.processed === 1 ? "" : "s"} added to the translation queue. Click "Run translation" to start.`,
				);
			}
			clearSelection();
			setLoading(true);
			setProducts([]);
			await fetchProducts(tabId);
			setLoading(false);
		} catch (err: unknown) {
			setActionError(
				err instanceof Error ? err.message : "Bulk action failed",
			);
		}
	}

	async function confirmClear() {
		setClearConfirmOpen(false);
		await bulkAction("clear");
	}

	async function handleStartTranslation() {
		setRunConfirmOpen(false);
		setTriggering(true);
		setRunSuccess(null);
		setRunError(null);

		try {
			const body: Record<string, unknown> = {
				max_products: parseInt(maxProducts, 10) || 50,
			};
			if (engineOverride) body.engine = engineOverride;

			const res = await authenticatedFetch<TriggerResponse>("/api/translate", {
				method: "POST",
				body: JSON.stringify(body),
			});
			// Optimistically mark currently-loaded pending products as running so the
			// UI reflects the kicked-off run immediately. The poller clears this set
			// when activeRun flips false, and refetches the real tag state.
			setRunningIds(
				new Set(
					products
						.filter((p) => p.tags.includes("translate-pending"))
						.map((p) => p.id),
				),
			);
			setRunSuccess(
				res.message ??
					"Translation started. The Pending tab will refresh automatically when it finishes.",
			);
			setActiveRun(true);
		} catch (err: unknown) {
			setRunError(
				err instanceof Error ? err.message : "Failed to start translation",
			);
			refreshUsage();
		} finally {
			setTriggering(false);
		}
	}

	const promotedBulkActions = [
		{
			content: usage?.blocked
				? "Queue for later"
				: "Add to translation queue",
			onAction: () => bulkAction("mark"),
		},
		{
			content: "Clear translation tags",
			onAction: () => setClearConfirmOpen(true),
		},
	];

	const clearCount = selectedResources.length;

	const plannedMaxProducts = parseInt(maxProducts, 10) || 50;
	const productsRemaining = usage
		? Math.max(usage.productLimit - usage.productsTranslated, 0)
		: null;
	const effectiveEngine = engineOverride || "default (from config)";

	const rowMarkup = products.map((product, index) => {
		const translatedTags = product.tags
			.filter((t) => t.startsWith("translated:"))
			.map((t) => (
				<Badge key={t} tone="info">
					{t.replace("translated:", "")}
				</Badge>
			));

		const pendingBadge = product.tags.includes("translate-pending") ? (
			<Badge tone="warning">translate-pending</Badge>
		) : null;
		// Server-truth: backend tags products it has skipped. Fall back to
		// client-side rule eval so users can preview ineligibility before a run.
		const markedIneligible = product.tags.includes("translate-ineligible");
		const previewReasons = markedIneligible
			? []
			: autoTranslateIneligibilityReasons(
					product,
					autoTranslateEnabled ? autoTranslateRules : null,
				);
		const showIneligible = markedIneligible || previewReasons.length > 0;
		const ineligibleTooltip = markedIneligible
			? "Skipped by the last translation run because the product did not meet the auto-translate rules."
			: previewReasons.join(" ");
		const statusBadge = translationStatusBadge(
			product,
			runningIds.has(product.id),
		);

		const translateAdaptUrl = buildTranslateAndAdaptUrl(
			shopDomain,
			product.id,
			targetLocale,
		);

		return (
			<IndexTable.Row
				id={product.id}
				key={product.id}
				selected={selectedResources.includes(product.id)}
				position={index}
			>
				<IndexTable.Cell>
					<Text variant="bodyMd" fontWeight="bold" as="span">
						{product.title}
					</Text>
				</IndexTable.Cell>
				<IndexTable.Cell>
					<InlineStack gap="100" wrap>
						{pendingBadge}
						{translatedTags}
					</InlineStack>
				</IndexTable.Cell>
				<IndexTable.Cell>
					<InlineStack gap="100" wrap>
						{statusBadge}
						{product.verifiedTranslation?.tagMismatch && <Badge tone="attention">Tag differs from translation</Badge>}
						{showIneligible && (
							<Tooltip content={ineligibleTooltip}>
								<Badge tone="critical">Ineligible</Badge>
							</Tooltip>
						)}
					</InlineStack>
				</IndexTable.Cell>
				<IndexTable.Cell>
					<Text as="span" tone="subdued">
						{formatDate(product.updatedAt)}
					</Text>
				</IndexTable.Cell>
				<IndexTable.Cell>
					{translateAdaptUrl ? (
						<span onClick={(e) => e.stopPropagation()}>
							<Link url={translateAdaptUrl} target="_blank">
								View
							</Link>
						</span>
					) : (
						<Text as="span" tone="subdued">
							—
						</Text>
					)}
				</IndexTable.Cell>
			</IndexTable.Row>
		);
	});

	if (loading) {
		return (
			<SkeletonPage title="Products">
				<Layout>
					<Layout.Section>
						<Card>
							<SkeletonBodyText lines={8} />
						</Card>
					</Layout.Section>
				</Layout>
			</SkeletonPage>
		);
	}

	const autoOn = autoTranslateEnabled;
	const nextRunLabel = schedule?.nextInvocationAt
		? formatNextRun(schedule.nextInvocationAt)
		: null;

	return (
		<Page
			title="Products"
			subtitle={syncSafe ? "Enroll products in your content manager. Translation status updates automatically." : "Queue products for translation, then run them when you're ready."}
			primaryAction={canManage ? {
				content: "Run translation",
				onAction: () => setRunConfirmOpen(true),
				disabled: triggering || (usage?.blocked ?? false),
				loading: triggering,
			} : undefined}
		>
			<Modal
				id="confirm-clear-tags-modal"
				open={clearConfirmOpen}
				onHide={() => setClearConfirmOpen(false)}
			>
				<TitleBar title="Clear translation tags?">
					<button variant="primary" tone="critical" onClick={confirmClear}>
						Clear tags
					</button>
					<button onClick={() => setClearConfirmOpen(false)}>Cancel</button>
				</TitleBar>
				<div style={{ padding: "1rem" }}>
					<p style={{ margin: 0 }}>
						Remove the <code>translate-pending</code> tag from{" "}
						<strong>{clearCount}</strong>{" "}
						product{clearCount === 1 ? "" : "s"}?
					</p>
					<p
						style={{
							marginTop: "0.75rem",
							color: "var(--p-color-text-secondary)",
						}}
					>
						These products will be skipped on the next run. You can re-add the
						tag at any time to queue them again.
					</p>
				</div>
			</Modal>

			<Modal
				id="confirm-translate-modal"
				open={runConfirmOpen}
				onHide={() => setRunConfirmOpen(false)}
			>
				<TitleBar title="Start translation run?">
					<button variant="primary" onClick={handleStartTranslation}>
						Start translation
					</button>
					<button onClick={() => setRunConfirmOpen(false)}>Cancel</button>
				</TitleBar>
				<div style={{ padding: "1rem" }}>
					<BlockStack gap="400">
						<TextField
							label={
								<Tooltip content={MAX_PRODUCTS_TOOLTIP} hasUnderline>
									<span>Products to translate</span>
								</Tooltip>
							}
							type="number"
							value={maxProducts}
							onChange={setMaxProducts}
							autoComplete="off"
							helpText="Maximum number of pending products to process in this run."
							min="1"
							max="500"
						/>
						<Select
							label={
								<Tooltip content={ENGINE_OVERRIDE_TOOLTIP} hasUnderline>
									<span>Engine override</span>
								</Tooltip>
							}
							options={ENGINE_OPTIONS}
							value={engineOverride}
							onChange={setEngineOverride}
							helpText="Override the translation engine for this run only."
						/>
						<Text as="p">
							About to translate up to{" "}
							<strong>{plannedMaxProducts}</strong>{" "}
							product{plannedMaxProducts === 1 ? "" : "s"} using the{" "}
							<strong>{effectiveEngine}</strong> engine.
						</Text>
						{usage && !usage.billingExempt && productsRemaining !== null && (
							<Text as="p">
								You have{" "}
								<strong>
									{productsRemaining.toLocaleString("en-GB")}
								</strong>{" "}
								product{productsRemaining === 1 ? "" : "s"} remaining on the{" "}
								<strong>{usage.planName}</strong> plan this month
								{plannedMaxProducts > productsRemaining ? (
									<>
										{" "}— this run will stop at{" "}
										<strong>
											{productsRemaining.toLocaleString("en-GB")}
										</strong>
										.
									</>
								) : (
									"."
								)}
							</Text>
						)}
					</BlockStack>
				</div>
			</Modal>

			<Layout>
				<Layout.Section>
					{syncSafe ? (
						<Banner tone="info" title="Translations managed from your content manager">
							<p>{syncSafeHelp(counts?.triggerTag, counts?.metafields)}</p>
						</Banner>
					) : autoOn ? (
						<Banner
							tone="success"
							title="Auto-Translate is ON"
							action={{
								content: "Manage rules",
								onAction: () => navigate("/auto-translate"),
							}}
						>
							<p>
								Products matching your rules are translated automatically.
								{nextRunLabel && (
									<>
										{" "}Next run at <strong>{nextRunLabel}</strong>.
									</>
								)}
							</p>
						</Banner>
					) : (
						<Banner
							tone="info"
							title="Auto-Translate is OFF"
							action={{
								content: "Enable Auto-Translate",
								onAction: () => navigate("/auto-translate"),
							}}
						>
							<p>
								Only products you queue below will be translated. Enable
								Auto-Translate to translate products automatically based on
								rules.
							</p>
						</Banner>
					)}
				</Layout.Section>

				{notInstalled && (
					<Layout.Section>
						<Banner
							tone="warning"
							title="App not installed"
							action={{
								content: "Reinstall app",
								url: notInstalled.installUrl,
								external: true,
							}}
						>
							<p>
								This shop is missing its access token. Reinstall the app to
								grant permissions, then reload this page.
							</p>
						</Banner>
					</Layout.Section>
				)}
				{error && !notInstalled && (
					<Layout.Section>
						<Banner tone="critical" title="Failed to load products">
							<p>{error}</p>
						</Banner>
					</Layout.Section>
				)}
				{actionError && (
					<Layout.Section>
						<Banner tone="critical" title="Action failed">
							<p>{actionError}</p>
						</Banner>
					</Layout.Section>
				)}
				{actionNotice && (
					<Layout.Section>
						<Banner tone="info" title="Added to queue" onDismiss={() => setActionNotice(null)}>
							<p>{actionNotice}</p>
						</Banner>
					</Layout.Section>
				)}
				{runSuccess && (
					<Layout.Section>
						<Banner
							tone="success"
							title="Translation started"
							onDismiss={() => setRunSuccess(null)}
						>
							<p>{runSuccess}</p>
						</Banner>
					</Layout.Section>
				)}
				{runError && (
					<Layout.Section>
						<Banner
							tone="critical"
							title="Failed to start translation"
							onDismiss={() => setRunError(null)}
						>
							<p>{runError}</p>
						</Banner>
					</Layout.Section>
				)}

				<EntitlementBanner usage={usage} onViewPlans={() => navigate("/plans")} />

				<Layout.Section>
					<Card padding="0">
						<Tabs
							tabs={tabs}
							selected={selectedTab}
							onSelect={(index) => {
								setSearchParams((previous) => { const next = new URLSearchParams(previous); next.set("status", TABS[index].id); return next; });
								clearSelection();
							}}
						>
							<BlockStack gap="0">
								{activeRun && (
									<div style={{ padding: "var(--p-space-300) var(--p-space-400) 0" }}>
										<InlineStack align="end">
											<Badge tone="info">
												Run active — refreshing every 10 s
											</Badge>
										</InlineStack>
									</div>
								)}
								<IndexTable
									resourceName={{
										singular: "product",
										plural: "products",
									}}
									itemCount={products.length}
									selectedItemsCount={
										allResourcesSelected
											? "All"
											: selectedResources.length
									}
									onSelectionChange={handleSelectionChange}
									selectable={canManage}
									promotedBulkActions={canManage ? promotedBulkActions : []}
									headings={[
										{ title: "Product" },
										{ title: "Tags" },
										{ title: "Translation Status" },
										{ title: "Last Updated" },
										{ title: "Translate & Adapt" },
									]}
									emptyState={
										<Text as="p" tone="subdued">
											No products found.
										</Text>
									}
								>
									{rowMarkup}
								</IndexTable>

								{hasNextPage && (
									<div
										style={{
											padding: "var(--p-space-400)",
											display: "flex",
											justifyContent: "center",
										}}
									>
										<Button
											onClick={handleLoadMore}
											loading={loadingMore}
										>
											Load more
										</Button>
									</div>
								)}
							</BlockStack>
						</Tabs>
					</Card>
				</Layout.Section>
			</Layout>
		</Page>
	);
}
